import { useState, useEffect } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Tooltip,
} from "@chakra-ui/react";

const BIRTH_DATE = new Date(2025, 0, 10); // 10 January 2025

const calculateAge = () => {
  const now = new Date();
  let years = now.getFullYear() - BIRTH_DATE.getFullYear();
  let months = now.getMonth() - BIRTH_DATE.getMonth();
  let days = now.getDate() - BIRTH_DATE.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalDays = Math.floor((now - BIRTH_DATE) / (1000 * 60 * 60 * 24));
  const totalMonths = years * 12 + months;

  const nextBirthday = new Date(now.getFullYear(), 0, 10);
  if (nextBirthday <= now) nextBirthday.setFullYear(now.getFullYear() + 1);
  const daysUntilBirthday = Math.ceil(
    (nextBirthday - now) / (1000 * 60 * 60 * 24)
  );

  return { years, months, days, totalDays, totalMonths, daysUntilBirthday };
};

// ─── Fun stats ───────────────────────────────────────────────
const getFunStats = (totalDays) => [
  {
    emoji: "😴",
    label: "Naps taken",
    value: Math.round(totalDays * 2.5).toLocaleString(),
    tooltip: "Babies nap ~2-3 times per day",
    color: "purple",
  },
  {
    emoji: "🍼",
    label: "Meals",
    value: Math.round(totalDays * 5).toLocaleString(),
    tooltip: "~5 meals/feeds per day",
    color: "green",
  },
  {
    emoji: "🧷",
    label: "Diapers",
    value: Math.round(totalDays * 6).toLocaleString(),
    tooltip: "~6 diaper changes per day",
    color: "orange",
  },
  {
    emoji: "💓",
    label: "Heartbeats",
    value: ((totalDays * 24 * 60 * 120) / 1000000).toFixed(1) + "M",
    tooltip: "Baby heart beats ~120 times/min",
    color: "red",
  },
  {
    emoji: "😊",
    label: "Smiles",
    value: Math.round(totalDays * 50).toLocaleString(),
    tooltip: "Babies smile ~50 times a day",
    color: "yellow",
  },
  {
    emoji: "🌙",
    label: "Sleeps",
    value: totalDays.toLocaleString(),
    tooltip: "One goodnight sleep every day",
    color: "blue",
  },
];

// ─── Milestone phase ─────────────────────────────────────────
const getMilestonePhase = (totalMonths) => {
  if (totalMonths < 3)
    return { phase: "Newborn Explorer", emoji: "👶", desc: "Discovering the world through senses" };
  if (totalMonths < 6)
    return { phase: "Tiny Grabber", emoji: "🤲", desc: "Reaching and grabbing everything!" };
  if (totalMonths < 9)
    return { phase: "Sitting Champion", emoji: "🪑", desc: "Sitting up and babbling away" };
  if (totalMonths < 12)
    return { phase: "Crawl Commander", emoji: "🐛", desc: "On the move — nothing is safe!" };
  if (totalMonths < 15)
    return { phase: "First Steps Hero", emoji: "🚶", desc: "Walking (and falling) with style" };
  if (totalMonths < 18)
    return { phase: "Word Collector", emoji: "🗣️", desc: "Learning new words every day" };
  if (totalMonths < 24)
    return { phase: "Mini Tornado", emoji: "🌪️", desc: "Running, climbing, unstoppable!" };
  if (totalMonths < 36)
    return { phase: "Little Adventurer", emoji: "🧭", desc: "Questions, imagination, wonder" };
  return { phase: "Growing Star", emoji: "⭐", desc: "Becoming his own person" };
};

// ─── Daily rotating facts ────────────────────────────────────
const ROTATING_FACTS = [
  "Babies can recognize their mother's voice from birth 🎵",
  "A baby's brain doubles in size in the first year 🧠",
  "Babies are born with ~300 bones (adults have 206) 🦴",
  "Newborns can only see 20-30cm clearly 👀",
  "Babies start dreaming before they are born 💭",
  "A baby's fingerprints form at 3 months in the womb 🖐️",
  "Babies can breathe and swallow at the same time 😮",
  "Newborns prefer to look at faces over anything else 👶",
  "Babies recognize music they heard in the womb 🎶",
  "A baby's first real smile usually appears at 6-8 weeks 😄",
  "Babies have more taste buds than adults 👅",
  "A baby's eyes are 75% of adult size at birth 👁️",
  "Babies laugh about 300 times a day 😂",
  "Newborns can smell their mother's milk from across the room 👃",
];

// ─── Sub-components ──────────────────────────────────────────
const AgeCard = ({ value, label, emoji, color }) => (
  <Box
    bg="whiteAlpha.100"
    border="1px solid"
    borderColor={`${color}.700`}
    borderRadius={{ base: "lg", md: "xl" }}
    px={{ base: 4, md: 6 }}
    py={{ base: 2, md: 3 }}
    textAlign="center"
    transition="all 0.3s"
    _hover={{
      transform: "translateY(-3px)",
      borderColor: `${color}.500`,
    }}
  >
    <Text
      fontSize={{ base: "2xl", md: "4xl" }}
      fontWeight="black"
      color={`${color}.300`}
      lineHeight="1"
    >
      {value}
    </Text>
    <Text
      fontSize={{ base: "2xs", md: "xs" }}
      color="gray.400"
      fontWeight="bold"
      mt={1}
      textTransform="uppercase"
      letterSpacing="wider"
    >
      {label} {emoji}
    </Text>
  </Box>
);

const FunStat = ({ emoji, label, value, tooltip, color }) => (
  <Tooltip label={tooltip} hasArrow placement="top">
    <Box
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="lg"
      px={{ base: 2, md: 3 }}
      py={{ base: 1.5, md: 2 }}
      textAlign="center"
      cursor="default"
      transition="all 0.2s"
      _hover={{ bg: "whiteAlpha.100", transform: "scale(1.05)" }}
    >
      <Text fontSize={{ base: "sm", md: "md" }}>{emoji}</Text>
      <Text
        fontSize={{ base: "sm", md: "md" }}
        fontWeight="bold"
        color={`${color}.300`}
      >
        {value}
      </Text>
      <Text fontSize="2xs" color="gray.500">
        {label}
      </Text>
    </Box>
  </Tooltip>
);

// ─── Main Component ──────────────────────────────────────────
const MattiasAgeTracker = () => {
  const [age, setAge] = useState(calculateAge);

  useEffect(() => {
    const interval = setInterval(() => setAge(calculateAge()), 86400000);
    return () => clearInterval(interval);
  }, []);

  const funStats = getFunStats(age.totalDays);
  const milestone = getMilestonePhase(age.totalMonths);
  const dailyFact = ROTATING_FACTS[age.totalDays % ROTATING_FACTS.length];

  return (
    <Box
      maxW="600px"
      mx="auto"
      textAlign="center"
      py={{ base: 3, md: 5 }}
      px={{ base: 2, md: 4 }}
    >
      <VStack spacing={{ base: 3, md: 4 }}>
        {/* Title */}
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="bold"
          bgGradient="linear(to-r, teal.300, cyan.400)"
          bgClip="text"
        >
          🌟 Mattias' Journey 🌟
        </Text>

        {/* Info badges */}
        <HStack spacing={2} flexWrap="wrap" justify="center">
          <Badge
            bg="whiteAlpha.100"
            color="gray.400"
            fontSize={{ base: "2xs", md: "xs" }}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            🎈 Born January 10, 2025
          </Badge>
          <Badge
            bg="whiteAlpha.100"
            color="gray.400"
            fontSize={{ base: "2xs", md: "xs" }}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            📆 {age.totalDays} days alive
          </Badge>
          {age.daysUntilBirthday <= 30 && (
            <Badge
              colorScheme="yellow"
              fontSize={{ base: "2xs", md: "xs" }}
              px={2}
              py={0.5}
              borderRadius="full"
            >
              🎂 Birthday in {age.daysUntilBirthday} days!
            </Badge>
          )}
        </HStack>

        {/* Age cards */}
        <HStack spacing={{ base: 2, md: 4 }} justify="center">
          <AgeCard value={age.years} label="Years" emoji="🎂" color="purple" />
          <AgeCard value={age.months} label="Months" emoji="📅" color="teal" />
          <AgeCard value={age.days} label="Days" emoji="🌞" color="cyan" />
        </HStack>

        {/* Current milestone phase */}
        <Box
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
          borderRadius="xl"
          px={{ base: 3, md: 4 }}
          py={{ base: 2, md: 3 }}
          w="full"
        >
          <Text fontSize={{ base: "xl", md: "2xl" }}>{milestone.emoji}</Text>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="bold"
            color="teal.300"
          >
            {milestone.phase}
          </Text>
          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.400">
            {milestone.desc}
          </Text>
        </Box>

        {/* Fun stats grid */}
        <SimpleGrid
          columns={{ base: 3, md: 6 }}
          spacing={{ base: 1.5, md: 2 }}
          w="full"
        >
          {funStats.map((stat) => (
            <FunStat key={stat.label} {...stat} />
          ))}
        </SimpleGrid>

        {/* Daily fun fact */}
        <Box
          bg="whiteAlpha.50"
          borderRadius="lg"
          px={{ base: 3, md: 4 }}
          py={{ base: 1.5, md: 2 }}
          w="full"
          border="1px dashed"
          borderColor="whiteAlpha.200"
        >
          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
            💡 Did you know?
          </Text>
          <Text
            fontSize={{ base: "xs", md: "sm" }}
            color="gray.300"
            fontStyle="italic"
          >
            {dailyFact}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default MattiasAgeTracker;